"use client";

/**
 * CollabV3TimeZone — 상세 전체가 쓰는 표시 시간대.
 * @description 장소 나라의 시간대를 알면 그것으로 찍는다 — 3.0 시각은 주최자가 그 나라 벽시계로 고른
 *   값이라 고른 그대로 나온다. 모르면(온라인 · 시간대가 여럿인 나라) **보는 사람의 시간대**다(앱과 같다).
 *
 *   보는 사람의 시간대는 서버가 알 수 없다. 서버와 첫 렌더는 UTC 로 찍고, 붙은 뒤 한 번 다시 찍는다 —
 *   `useSyncExternalStore` 가 하이드레이션 동안 서버 값을 쓰므로 문자열이 어긋나지 않는다.
 * @example
 * <CollabV3TimeZoneProvider placeTimeZone="Asia/Seoul">{children}</CollabV3TimeZoneProvider>
 */
import { createContext, useContext, useSyncExternalStore } from "react";

import type { CollabV3TimeZoneProviderProps } from "./CollabV3Detail.types";

const FALLBACK_TIME_ZONE = "UTC";

const TimeZoneContext = createContext<string>(FALLBACK_TIME_ZONE);

const subscribe = () => () => {};
const readDeviceTimeZone = (): string | null =>
  Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
const readServerTimeZone = (): string | null => null;

export function CollabV3TimeZoneProvider({
  placeTimeZone,
  children,
}: CollabV3TimeZoneProviderProps) {
  const deviceTimeZone = useSyncExternalStore(
    subscribe,
    readDeviceTimeZone,
    readServerTimeZone,
  );
  const timeZone = placeTimeZone ?? deviceTimeZone ?? FALLBACK_TIME_ZONE;
  return (
    <TimeZoneContext.Provider value={timeZone}>
      {children}
    </TimeZoneContext.Provider>
  );
}

/**
 * 지금 표시 시간대.
 * @example const timeZone = useCollabV3TimeZone(); formatDay(iso, timeZone)
 */
export const useCollabV3TimeZone = (): string => useContext(TimeZoneContext);
